import {
  createDuplicateScanGuard,
  type DuplicateScanGuard,
} from './duplicateScanGuard';

export interface ScanInputKeyEvent {
  key: string;
  ctrlKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  preventDefault(): void;
}

export interface ScanInputPipelineObserver {
  onDuplicate?(input: string, elapsedMs: number): void;
  onFinalize?(input: string, inputKind: 'url' | 'code'): void;
}

export interface ScanInputPipelineOptions {
  onScan(input: string): void;
  isBusy?: () => boolean;
  // Optional completeness predicate. When set, Enter only force-finalizes a
  // buffer that already looks like a complete code; an incomplete buffer is left
  // to the idle auto-submit timer, so a fast keyboard-wedge whose Enter outruns
  // the final character's input event can't submit a one-char-short value.
  // Defaults to "always complete" (Enter finalizes immediately, legacy behavior).
  isComplete?: (input: string) => boolean;
  autoSubmitDelayMs?: number;
  duplicatePreventionWindowMs?: number;
  now?: () => number;
  setTimeout?: (callback: () => void, ms: number) => number;
  clearTimeout?: (timer: number) => void;
  observer?: ScanInputPipelineObserver;
}

const DEFAULT_AUTO_SUBMIT_DELAY_MS = 500;

export class ScanInputPipeline {
  private readonly onScan: (input: string) => void;
  private readonly isBusy: () => boolean;
  private readonly isComplete?: (input: string) => boolean;
  private readonly autoSubmitDelayMs: number;
  private readonly duplicates: DuplicateScanGuard;
  private readonly setTimer: (callback: () => void, ms: number) => number;
  private readonly clearTimer: (timer: number) => void;
  private readonly observer?: ScanInputPipelineObserver;
  private submitTimer: number | null = null;

  constructor(options: ScanInputPipelineOptions) {
    this.onScan = options.onScan;
    this.isBusy = options.isBusy ?? (() => false);
    this.isComplete = options.isComplete;
    this.autoSubmitDelayMs =
      options.autoSubmitDelayMs ?? DEFAULT_AUTO_SUBMIT_DELAY_MS;
    this.duplicates = createDuplicateScanGuard(
      options.duplicatePreventionWindowMs,
      options.now
    );
    this.setTimer =
      options.setTimeout ??
      ((callback, ms) => globalThis.window.setTimeout(callback, ms));
    this.clearTimer =
      options.clearTimeout ??
      ((timer) => globalThis.window.clearTimeout(timer));
    this.observer = options.observer;
  }

  handleInput(input: string): void {
    this.processBufferedInput(input);
  }

  handleKeydown(event: ScanInputKeyEvent, input: string): void {
    if (event.ctrlKey || event.altKey || event.metaKey) return;

    if (event.key !== 'Enter') return;

    event.preventDefault();
    const normalized = normalizeScanInput(input);
    // A fast wedge can fire Enter before the trailing character's input event
    // commits, leaving the buffer one char short. Only treat Enter as
    // authoritative when the buffer already parses as a complete code; otherwise
    // leave the idle auto-submit timer to finalize the settled buffer once the
    // last character lands (or to surface a genuinely-truncated scan).
    if (normalized && this.isComplete && !this.isComplete(normalized)) {
      return;
    }
    this.cancelSubmitTimer();
    this.processBufferedInput(input, true);
  }

  dispose(): void {
    this.cancelSubmitTimer();
    this.resetDuplicateGuard();
  }

  // The duplicate guard absorbs a scanner double-fire of ONE physical scan; it
  // must not swallow a legitimate re-scan of the same ticket after a failed
  // lookup. The controller clears it when a lookup completes (busy → idle) and
  // on screen teardown, so an immediate retry of a valid code is never dropped.
  resetDuplicateGuard(): void {
    this.duplicates.reset();
  }

  private processBufferedInput(input: string, force = false): void {
    if (this.isBusy()) return;

    const normalizedInput = normalizeScanInput(input);
    if (!normalizedInput) {
      this.cancelSubmitTimer();
      return;
    }

    if (force) {
      this.finalizeScan(normalizedInput);
      return;
    }

    this.cancelSubmitTimer();
    this.submitTimer = this.setTimer(() => {
      this.submitTimer = null;
      this.finalizeScan(normalizedInput);
    }, this.autoSubmitDelayMs);
  }

  private finalizeScan(input: string): void {
    if (!input || this.isBusy()) return;

    const elapsedMs = this.duplicates.check(input);
    if (elapsedMs !== null) {
      this.observer?.onDuplicate?.(input, elapsedMs);
      return;
    }

    this.observer?.onFinalize?.(input, classifyScanInput(input));
    this.onScan(input);
  }

  private cancelSubmitTimer(): void {
    if (this.submitTimer === null) return;
    this.clearTimer(this.submitTimer);
    this.submitTimer = null;
  }
}

export function normalizeScanInput(input: string): string {
  return String(input)
    .trim()
    .replace(/[\r\n\t]+/g, '');
}

export function classifyScanInput(input: string): 'url' | 'code' {
  if (/^https?:\/\//i.test(input) || input.includes('/')) return 'url';
  return 'code';
}
